import { APP_BASE_HREF } from '@angular/common';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorHandler, Injectable, Injector, NgModule } from '@angular/core';
import { BrowserModule, HammerModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { AwsRum, AwsRumConfig } from 'aws-rum-web';
import { TranslateLoader, TranslateModule, TranslateParser, TranslateService, TranslateStore } from '@ngx-translate/core';
import Aura from '@primeng/themes/aura';
import { createTranslateLoader, createTranslateParser } from 'app/app.providers';
import { MiradiTranslateStore } from 'app/shared/services/translate/miradi-translate.store';
import { AppService, BASE_PATH, GlobalBusyService, NotificationService } from 'app/shared/services';
import { environment } from 'environments/environment';
import { NgCircleProgressModule } from 'ng-circle-progress';
import { DateFnsModule } from 'ngx-date-fns';
import { MessageService, TreeDragDropService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { ToastModule } from 'primeng/toast';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppSharedModule } from './shared/app-shared.module';
import { EulaBannerComponent } from './shared/components/eula-banner/eula-banner.component';
import { MaintenanceBannerComponent } from './shared/components/maintenance-banner/maintenance-banner.component';
import { SessionExpiredDialogComponent } from './shared/components/session-expired-dialog/session-expired-dialog.component';
import { SubscriptionNudgeBannerComponent } from './shared/components/subscription-nudge-banner/subscription-nudge-banner.component';
import { SurveyBannerComponent } from './shared/components/survey-banner/survey-banner.component';
import { ChunkLoadUtils } from './shared/utils/chunk-load.utils';
const { name, version } = require('../../package.json');



if (environment.rumAppId) {
  initCloudWatchRUM();
}

function initCloudWatchRUM() {
  // Parse session sample rate with fallback to 10% sampling
  const parsedRate = parseFloat(environment.rumSessionSampleRate);
  const sessionSampleRate = !isNaN(parsedRate) ? parsedRate : 0.1;

  if (isNaN(parsedRate)) {
    console.warn('RUM_SESSION_SAMPLE_RATE not configured, using fallback 0.1 (10% sampling)');
  }

  // Build RUM endpoint URL and exclusion pattern using configured AWS region
  const rumEndpoint = `https://dataplane.rum.${environment.awsRegion}.amazonaws.com`;
  const rumUrlExclusionPattern = new RegExp(`dataplane\\.rum\\.${environment.awsRegion.replace(/[-/]/g, '\\$&')}\\.amazonaws\\.com`);

  // The X-Ray trace header is stamped on requests this page makes, and a
  // cross-origin server has to name x-amzn-trace-id in Access-Control-Allow-Headers
  // or the browser refuses the preflight. Tableau's embed sign-in does not name
  // it, so a sampled session used to break the embedded dashboard while an
  // unsampled one worked. The header only buys tracing into our own backend, so
  // it goes on same-origin requests only: a relative path, or this origin.
  // Matched against the raw request URL, which for relative calls is the path.
  const sameOriginRequests = [
    /^\/(?!\/)/,
    new RegExp(`^${window.location.origin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?:[/?#]|$)`),
  ];

  const config: AwsRumConfig = {
    sessionSampleRate: sessionSampleRate,
    identityPoolId: environment.rumIdentityPoolId,
    endpoint: rumEndpoint,
    telemetries: [
      'errors',
      'performance',
      ['http', {
        addXRayTraceIdHeader: sameOriginRequests,
        recordAllRequests: true,
        urlsToExclude: [
          rumUrlExclusionPattern,
          // Exclude user session check - returns 401 for anonymous users (expected behavior)
          /\/uxapi\/v1\/user$/
        ]
      }]
    ],
    allowCookies: true,
    enableXRay: true,
    signing: true
  };

  const APPLICATION_ID = environment.rumAppId;
  const APPLICATION_VERSION = version;
  const APPLICATION_REGION = environment.awsRegion;

  const awsRum = new AwsRum(
    APPLICATION_ID,
    APPLICATION_VERSION,
    APPLICATION_REGION,
    config
  );

  (window as any).awsRum = awsRum;
}



@Injectable()
export class GlobalErrorHandler implements ErrorHandler {

  private globalBusyService: GlobalBusyService;
  private notificationService: NotificationService;
  private translateService: TranslateService;

  constructor(
    private injector: Injector,
  ) {
    this.globalBusyService = this.injector.get(GlobalBusyService);
    this.notificationService = this.injector.get(NotificationService);
    this.translateService = this.injector.get(TranslateService);
  }

  handleError(error: any) {
    // Stale-bundle recovery: a lazy chunk 404s after a deploy -> auto-reload onto the new build.
    // Catches non-router dynamic imports too (router NavigationError is handled in app.component).
    if (ChunkLoadUtils.isChunkLoadError(error)) {
      if (!ChunkLoadUtils.reloadForNewBuild()) {
        this.offerChunkRetry(error);
      }
      return;
    }

    if (
      error &&
      error.status !== 401 && // Unauthorized
      error.status !== 403 && // Forbidden
      error.status !== 404 && // Not Found (can easily happen when looking for a unexisting .po file)
      (error.message || '').indexOf('ViewDestroyedError: Attempt to use a destroyed view: detectChanges') < 0 && // issue in the ngx-loading package...waiting for it to be updated.
      (error.message || '').indexOf('ExpressionChangedAfterItHasBeenCheckedError') < 0 // this only happens in dev angular build - I'm sure
    ) {
      // IE Bug
      if ((error.message || '').indexOf('available to complete this operation.') >= 0) {
        this.notificationService.error(
          this.translateService.instant('Internet Explorer Error'),
          error.message,
        );
      }

      console.error(error);

      if ((window as any).awsRum) {
        (window as any).awsRum.recordError(error.originalError || error);
      }
    } else if (error) {
      console.warn(error);
      this.globalBusyService.setBusy(false);
    }
  }

  // A chunk failure outside the router has no route to fall back to, and the reload budget is
  // spent, so the user has to be the one who decides to try again. Logging alone leaves them on a
  // page that silently stopped working.
  private offerChunkRetry(error: any) {
    console.error(error);
    if ((window as any).awsRum) {
      (window as any).awsRum.recordError(error.originalError || error);
    }

    this.globalBusyService.setBusy(false);

    // Resolved here rather than in the constructor: ErrorHandler is built before the app, and
    // AppService pulls in the HTTP stack. There is no chunk to fail until both exist.
    const dialog = this.injector.get(AppService).globalInfoDialog;
    if (!dialog) {
      this.notificationService.error(
        this.translateService.instant('Network Error'),
        this.translateService.instant('Sorry we were unable to load the page you were trying to visit.'),
      );
      return;
    }

    dialog.show({
      title: this.translateService.instant('Network Error'),
      message: this.translateService.instant('Sorry we were unable to load the page you were trying to visit.'),
      buttonText: this.translateService.instant('Retry'),
    });
    dialog.onClose = () => {
      window.location.reload();
    };
  }

}

@NgModule({
  bootstrap: [
    AppComponent,
  ],
  declarations: [
    AppComponent,
  ],
  imports: [
    AppRoutingModule,
    AppSharedModule,
    BrowserModule,
    BrowserAnimationsModule,
    DateFnsModule.forRoot(),
    HammerModule,
    EulaBannerComponent,
    MaintenanceBannerComponent,
    SessionExpiredDialogComponent,
    NgCircleProgressModule.forRoot({
      animation: false,
      maxPercent: 100,
      outerStrokeWidth: 12,
      outerStrokeColor: '#A970AD', // '#3496A1', // 'green',
      outerStrokeLinecap: 'butt',
      innerStrokeColor: '#e7e8ea',
      innerStrokeWidth: 12,
      radius: 6,
      space: -12,
      showBackground: false,
      showTitle: false,
      showSubtitle: false,
      showUnits: false,
      responsive: false,
    }),
    SubscriptionNudgeBannerComponent,
    SurveyBannerComponent,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient]
      },
      parser: {
        provide: TranslateParser,
        useFactory: createTranslateParser,
      }
    }),
    ToastModule,
  ],
  providers: [
    {
      provide: APP_BASE_HREF,
      useValue: environment.appBaseHref,
    },
    {
      provide: BASE_PATH,
      useValue: environment.environmentBaseUrl + environment.apiBaseUrl,
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    MessageService,
    TreeDragDropService,
    provideAnimationsAsync(),
    providePrimeNG({
        theme: {
          preset: Aura,
          options: {
            darkModeSelector: false,
          }
        }
    }),
    provideHttpClient(withInterceptorsFromDi()),
    { provide: TranslateStore, useClass: MiradiTranslateStore },
  ],
})
export class AppModule { }
